import { usePostHog } from 'posthog-js/react';
import { Suspense, useEffect, useState } from 'react';
import { BiChevronLeft } from 'react-icons/bi';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { getServiceId, setServiceId } from '@/api/tokens.helper';
import { Drawer, OfflineIndicator } from '@/components';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { EdgeSwipeBack } from '@/components/EdgeSwipeBack/EdgeSwipeBack';
import { GlobalSearch } from '@/components/GlobalSearch';
import { DesktopHeaderUserArea } from '@/components/Header/DesktopHeaderUserArea';
import { HolidaySuggestionMount } from '@/components/HolidaySuggestion';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import { NotificationsSidePanel } from '@/components/NotificationsSidePanel';
import TaskNotifier from '@/components/TaskNotifier/TaskNotifier';
import { useFoldPosture } from '@/hooks/useFoldPosture';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useKeyboardAvoidance } from '@/hooks/useKeyboardAvoidance';
import { usePrefetchEndpoints } from '@/hooks/usePrefetchEndpoints';
import { useThemePreference } from '@/hooks/useThemePreference';
import { WebSocketProvider } from '@/providers';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { getBackHandler, useBackHandlerStore } from '@/store/back-handler/back-handler.store';
import { useEvents } from '@/store/events/events.store';
import { FirebaseProvider } from '@/store/notification-settings/push-notifications-provider';
import { NotificationsProvider } from '@/store/notifications/notifications-provider';
import {
  initActiveAccount,
  resolveActiveAccount,
} from '@/store/service-providers/current-service-provider.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { usePushNotifications } from '@/usePushNotification';
import { setRedirectAfterAuth } from '@/utils/auth-redirect';
import MaintainAIChatMount from '@pages/Equipment/components/EquipmentAIChat/MaintainAIChatMount';

import styles from './Layout.module.scss';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const { isLoggedIn, isInitializing, session, me } = useAuth();
  const switching = useActiveAccount((state) => state.switching);
  const generation = useActiveAccount((state) => state.generation);
  usePrefetchEndpoints(session);
  const { myVessels } = useVessels();
  const { fetchOutOfWaterStatuses } = useEvents();
  const [isLoading, setIsLoading] = useState(true);

  usePushNotifications();
  useKeyboardAvoidance();
  useThemePreference();

  useEffect(() => {
    if (import.meta.env.PROD) {
      window.gtag?.('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
      });
    }
  }, []);

  useEffect(() => {
    const loadSession = async () => {
      // Wait for auth initialization to finish — if we render before it completes,
      // ProtectedRoute redirects with stale permissions before we can switch accounts.
      if (isInitializing) return;

      if (isLoggedIn && !session) {
        try {
          await me();
        } catch (error) {
          console.error('Failed to load session in Layout:', error);
          navigate('/auth');
        }
      }

      // Handle deep-link service_id before rendering so ProtectedRoute sees correct permissions.
      const loadedSession = useAuth.getState().session;
      const targetServiceId = new URLSearchParams(window.location.search).get('service_id');
      if (targetServiceId && loadedSession?.services) {
        const ownedService = loadedSession.services.find((s) => s.service_id === targetServiceId);
        if (ownedService && targetServiceId !== getServiceId()) {
          const serviceType: 'vessel' | 'service-provider' =
            ownedService.service_type === 'vessel' ? 'vessel' : 'service-provider';
          useActiveAccount.setState({ serviceId: targetServiceId, serviceType });
          setServiceId(targetServiceId);
          await useAuth.getState().updateRoleForVessel(targetServiceId);
          useVessels.setState({ myVessels: undefined, vessel: undefined });
          const url = new URL(window.location.href);
          url.searchParams.delete('service_id');
          window.history.replaceState(null, '', url.toString());
        }
      }

      // Settle which service is active before any route paints, for the same reason the
      // session is awaited above: ProtectedRoute asks whether this viewer owns the selected
      // vessel, and an unresolved account reads as "not the owner" — a redirect the guard
      // cannot take back once the answer arrives. The deep-link branch above has already
      // set it when it ran, so this only covers the ordinary load.
      if (!useActiveAccount.getState().serviceType && loadedSession?.services) {
        resolveActiveAccount(loadedSession.services);
      }

      setIsLoading(false);
    };

    void loadSession();
  }, [isLoggedIn, session, isInitializing, me, navigate]);

  useEffect(() => {
    if (!session) return;
    const email = session.contacts?.find((c) => c.contact_type === 'email')?.contact;
    if (email) {
      posthog.identify(email, { email });
    }
  }, [session, posthog]);

  useEffect(() => {
    if (session?.services && !myVessels) {
      void initActiveAccount(session.services);
    }
  }, [session, myVessels]);

  useEffect(() => {
    if (!myVessels || myVessels.length === 0) return;
    void fetchOutOfWaterStatuses(myVessels.map((v) => v.id));
  }, [myVessels, fetchOutOfWaterStatuses]);

  if (isLoading) {
    return null;
  }

  // Signed out: redirect before the shell mounts. On mount, Drawer calls /me and the
  // notifications panel calls notifications, and both are exempt from the missing-service-id
  // cancel. With no token they get 401s, the refresh fails, and api.ts hard-reloads onto
  // /auth a second time. ProtectedRoute below redirects too, but only after the shell has
  // committed.
  if (!isLoggedIn) {
    setRedirectAfterAuth(location.pathname + location.search);
    return <Navigate to="/auth" replace />;
  }

  // Funnel not-yet-onboarded users to the dedicated onboarding page before any
  // in-app route paints. session is already resolved here (isLoading gated it),
  // so there's no dashboard flicker. /onboarding lives outside Layout — no loop.
  if (session && !session.onboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <WebSocketProvider>
      <FirebaseProvider />
      <NotificationsProvider>
        <LayoutContainer>
          {/* The route below is code-split: every page is behind `lazy()` in the
              router, so the shell needs one boundary to hold while a chunk lands.
              Keyed on the account switch: the page mounts afresh for the new service
              and its widgets load their own data (see refreshAfterAccountSwitch). */}
          {switching ? (
            <LoaderSpinner />
          ) : (
            <Suspense key={generation} fallback={<LoaderSpinner />}>
              <Outlet />
            </Suspense>
          )}
          <TaskNotifier />
          <HolidaySuggestionMount />
        </LayoutContainer>
      </NotificationsProvider>
    </WebSocketProvider>
  );
};

export default Layout;

/**
 * The iPhone Duo rail (useFoldPosture): back, the header's account switch, search and bell,
 * and the tab bar, stacked down the screen's outer edge in place of the bottom bar and each
 * page's header actions. Mounted only on a rail posture, so no other device subscribes to the
 * back stack.
 */
const FoldRail = () => {
  const navigate = useNavigate();
  // Re-render when a page registers or drops its back handler, and when the path changes: a
  // nested path with history behind it goes back without one. The handler itself is looked up
  // on click: a page swaps it on every render without telling the store.
  useBackHandlerStore((s) => s.stack);
  useLocation();
  const canGoBack = Boolean(getBackHandler(navigate));

  return (
    <div className={styles.foldRail}>
      {/* Disabled rather than removed, so the rail's slots stay put. */}
      <button
        type="button"
        className={styles.foldBack}
        onClick={() => void getBackHandler(navigate)?.()}
        disabled={!canGoBack}
        aria-label="Back"
      >
        <BiChevronLeft aria-hidden="true" />
      </button>
      <DesktopHeaderUserArea />
      <BottomBar />
    </div>
  );
};

export const LayoutContainer = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useIsMobile();
  const fold = useFoldPosture();

  return (
    <div className={`${styles.appShell} ${isMobile ? '' : styles.sidebarExpanded}`}>
      <Drawer />
      <main id="main-content" className={styles.mainContent}>
        <OfflineIndicator />
        {children}
      </main>
      {/* Outside the page: it floats over it and stays put while a swipe moves the page. */}
      <MaintainAIChatMount />
      {fold ? <FoldRail /> : isMobile && <BottomBar />}
      <NotificationsSidePanel />
      {/* Mounted once for the whole app: the triggers live in the per-page
          headers, and the Cmd-K binding has to work on every screen. */}
      <GlobalSearch />
      <EdgeSwipeBack />

      {/*<Button borderRadius="xl" className={styles.addButton}>*/}
      {/*  +*/}
      {/*</Button>*/}
    </div>
  );
};
